import { useEffect, useRef } from 'react';
import { usePower } from '../state/PowerContext';
import { playAlertSound } from '../utils/alertSound';
import { notifyDiscord } from '../api';
import { weatherConfidence, daysBetween } from '../utils/confidence';
import { todayISO } from '../utils/billingPeriod';
import { DANGER_THRESHOLD, WARNING_THRESHOLD } from '../utils/thresholds';

// What fraction of the period high counts as alert-worthy. 'danger' matches
// exactly the grid's red blocks — real risk of a new high.
function effectiveThreshold(alerts) {
  if (alerts.triggerLevel === 'warning') return WARNING_THRESHOLD;
  if (alerts.triggerLevel === 'custom') return (alerts.thresholdPct ?? 96) / 100;
  return DANGER_THRESHOLD;
}

function keyOf(r) { return `${r.date}_${r.hour}`; }

function hourLabel(h) {
  const hh = ((h - 1) % 12) + 1;
  const ampm = h >= 12 && h < 24 ? 'PM' : 'AM';
  return `${hh}${ampm}`;
}

function dateLabel(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

// Convert (date, hour-of-day 1..24) to a Date at hour-start (local time).
function hourStart(dateISO, hour) {
  const d = new Date(`${dateISO}T${String(Math.max(0, Math.min(23, hour - 1))).padStart(2, '0')}:00:00`);
  return d;
}

// AlertEngine is an invisible component that, given the live upcoming hours,
// fires user-configured notifications: Discord webhook, browser Notification,
// tab-title flash, and a repeating sound. Dedupes per (date,hour,series) so
// the user gets ONE kickoff alert + periodic reminders, not spam.
export default function AlertEngine({ upcoming, high, hiddenRisk }) {
  const { series, alerts, activePeriod } = usePower();
  const trackedRef = useRef(new Map()); // key -> { firedAt, lastReminderAt, firedDiscord }
  const titleTimerRef = useRef(null);
  const flashingRef = useRef(false);
  const origTitleRef = useRef(document.title);

  // Ask for browser-notification permission lazily the first time alerts.enabled is true
  useEffect(() => {
    if (alerts.enabled && alerts.browser?.enabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [alerts.enabled, alerts.browser?.enabled]);

  // Main tick — runs every 30 s
  useEffect(() => {
    if (!alerts.enabled || !high?.value) {
      // Stop title flashing if disabled
      if (titleTimerRef.current) { clearInterval(titleTimerRef.current); titleTimerRef.current = null; document.title = origTitleRef.current; flashingRef.current = false; }
      trackedRef.current.clear();
      return;
    }

    const threshold = effectiveThreshold(alerts);
    const leadMs = (alerts.leadTimeHours ?? 12) * 3_600_000;
    const reminderMs = (alerts.reminderMinutes ?? 60) * 60_000;

    const tick = () => {
      const now = Date.now();
      const tracked = trackedRef.current;

      // Find upcoming hours that qualify (>= threshold of period high) AND
      // whose hour-start is within leadMs from now.
      const active = (upcoming || []).filter(r => {
        const pct = r.value / high.value;
        if (pct < threshold) return false;
        const hs = hourStart(r.date, r.hour).getTime();
        return hs >= now && hs - now <= leadMs;
      });

      // Fire initial + reminder alerts
      // De-dupe state is keyed per billing view — each view has its own
      // period high, so the same hour can legitimately alert in one view
      // and not another.
      for (const r of active) {
        const k = `${keyOf(r)}_${series}_${activePeriod.id}`;
        const state = tracked.get(k);
        const hs = hourStart(r.date, r.hour).getTime();
        const hoursUntil = Math.max(0, Math.round((hs - now) / 3_600_000));
        const pct = Math.round((r.value / high.value) * 100);
        const msg = `**${series} peak risk** — ${dateLabel(r.date)} at ${hourLabel(r.hour)} (~${hoursUntil}h away) projected ${r.value.toFixed(0)} (${pct}% of period high ${high.value.toFixed(0)})${r.lmp != null ? ` · $${r.lmp.toFixed(2)}/MWh` : ''}${r.temperature != null ? ` · ${Math.round(r.temperature)}°F` : ''}. Consider curtailing.`;

        if (!state) {
          fireAll(msg, alerts, { tag: k });
          tracked.set(k, { firedAt: now, lastReminderAt: now, firedDiscord: alerts.discord.enabled });
          startTitleFlash(`⚠ ${pct}% RISK · ${hourLabel(r.hour)} ${dateLabel(r.date)}`);
        } else if (now - state.lastReminderAt >= reminderMs) {
          firePeriodic(msg, alerts, { tag: k });
          state.lastReminderAt = now;
        }
      }

      // Weather-inferred alerts — separate toggle, confidence-weighted.
      const wcfg = alerts.weather || {};
      if (wcfg.includeInAlerts && Array.isArray(hiddenRisk) && hiddenRisk.length && high?.value) {
        const today = todayISO();
        for (const r of hiddenRisk) {
          const days = daysBetween(today, r.future_date);
          if (days < 0 || days > (wcfg.maxDaysAhead ?? 10)) continue;
          const conf = weatherConfidence(days);
          const baseline = r.historical_p90_power ?? r.historical_max_power;
          const effective = baseline * conf;
          if (effective < high.value * ((wcfg.thresholdPct ?? 100) / 100)) continue;

          const k = `W_${r.future_date}_${r.future_peak_hour}_${series}_${activePeriod.id}`;
          const state = tracked.get(k);
          const hs = hourStart(r.future_date, r.future_peak_hour).getTime();
          if (hs <= now) continue;
          const pct = Math.round((effective / high.value) * 100);
          const msg = `**🌡 Weather-inferred risk** — ${dateLabel(r.future_date)} at ${hourLabel(r.future_peak_hour)} (${days}d out, ${Math.round(conf * 100)}% conf) forecast peak ${r.future_peak_temp_f}°F · 9 of 10 similar historical days peaked at/under ${baseline.toFixed(0)} (effective ≈${effective.toFixed(0)}, ${pct}% of period high ${high.value.toFixed(0)}).`;

          if (!state) {
            fireAll(msg, alerts, { tag: k, weather: true });
            tracked.set(k, { firedAt: now, lastReminderAt: now });
          } else if (now - state.lastReminderAt >= reminderMs) {
            firePeriodic(msg, alerts, { tag: k, weather: true });
            state.lastReminderAt = now;
          }
        }
      }

      // Purge tracked entries whose hour has passed
      for (const [k, state] of tracked) {
        const [dateHour] = k.split('_').slice(0, 2);
        // k is `YYYY-MM-DD_HH_SERIES` - split again
        const parts = k.split('_');
        const dISO = parts[0];
        const hr = Number(parts[1]);
        const hs = hourStart(dISO, hr).getTime();
        if (now > hs) {
          tracked.delete(k);
        }
      }

      // Stop flashing once there are no active alerts
      if (active.length === 0 && flashingRef.current) {
        if (titleTimerRef.current) clearInterval(titleTimerRef.current);
        titleTimerRef.current = null;
        document.title = origTitleRef.current;
        flashingRef.current = false;
      }
    };

    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [alerts, series, upcoming, high, hiddenRisk, activePeriod.id]);

  function startTitleFlash(flashText) {
    if (!alerts.tabFlash) return;
    if (flashingRef.current) return;
    flashingRef.current = true;
    let on = false;
    titleTimerRef.current = setInterval(() => {
      on = !on;
      document.title = on ? flashText : origTitleRef.current;
    }, 900);
  }

  function browserNotify(msg, alerts, info = {}) {
    const b = alerts.browser || {};
    if (!b.enabled) return;
    if (info.weather && !b.includeWeather) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      new Notification(
        info.weather ? 'Peak Watch — weather-inferred risk' : 'Peak Watch — curtailment suggested',
        {
          body: msg.replace(/\*\*/g, ''),
          tag: info.tag || 'peakwatch', // per-hour tag so simultaneous windows don't replace each other
          requireInteraction: !!b.requireInteraction,
        });
    } catch {}
  }

  function fireAll(msg, alerts, info = {}) {
    if (alerts.sound.enabled) playAlertSound(alerts.sound.name, alerts.sound.volume);
    browserNotify(msg, alerts, info);
    if (alerts.discord.enabled && alerts.discord.webhookUrl) {
      notifyDiscord(alerts.discord.webhookUrl, msg, alerts.discord.mentionUserId).catch(() => {});
    }
  }

  function firePeriodic(msg, alerts, info = {}) {
    if (alerts.sound.enabled) playAlertSound(alerts.sound.name, alerts.sound.volume);
    if (alerts.browser?.repeatWithReminders) browserNotify(msg, alerts, info);
  }

  return null;
}
